import type { AuthClaim, AuthStart } from '@gardener/protocol';

/**
 * The server (ADR-0024): `VITE_SERVER_URL` in production (https://backend.garden-wars.io), else this page's origin, which
 * Vite proxies to it.
 */
const API = (import.meta.env.VITE_SERVER_URL ?? '').replace(/\/+$/, '');
/** A kept build's replay player (ADR-0234): that build's own client on the server's origin, in its `?replay` mode. */
export const replayPlayer = (build: string): string => `${API}/replay/${build}/?replay`;
/** The callback's page posts its claim from the API's origin. */
const apiOrigin = () => new URL(API || location.href).origin;

const TOKEN = 'gardener.admin.token';
/** A Discord sign-in's key while it runs: the callback's claim is claimed with it (here, or in a tab `#auth=` opens). */
const KEY = 'gardener.admin.auth';

/** A failed call: `message` is the server's `error`. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** This browser's token: the device token of the account it signed in as (an admin's, `GET /api/admin/me` says). */
export const token = {
  get: (): string | null => localStorage.getItem(TOKEN),
  set: (t: string | null) => (t ? localStorage.setItem(TOKEN, t) : localStorage.removeItem(TOKEN)),
};

/** A JSON call with the token; a non-2xx answer throws `ApiError`. */
export async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const t = token.get();
  const headers: Record<string, string> = {};
  if (t) headers.authorization = `Bearer ${t}`;
  if (body !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(API + path, { method, headers, body: body === undefined ? null : JSON.stringify(body) });
  const json = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  if (!res.ok) throw new ApiError(res.status, json?.code ?? 'error', json?.error ?? res.statusText);
  return json as T;
}

/** A file the admin API answers (a map's tile, ADR-0223) with the token: its bytes; a non-2xx answer throws `ApiError`. */
export async function blob(path: string): Promise<Blob> {
  const t = token.get();
  const res = await fetch(API + path, t ? { headers: { authorization: `Bearer ${t}` } } : {});
  if (!res.ok) throw new ApiError(res.status, 'error', res.statusText);
  return res.blob();
}

/** A file put where the admin API keeps it (a map's tile, ADR-0223) with the token; a non-2xx answer throws `ApiError`. */
export async function put(path: string, body: Blob): Promise<void> {
  const t = token.get();
  const headers: Record<string, string> = { 'content-type': body.type || 'application/octet-stream' };
  if (t) headers.authorization = `Bearer ${t}`;
  const res = await fetch(API + path, { method: 'PUT', headers, body });
  if (res.ok) return;
  const json = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  throw new ApiError(res.status, json?.code ?? 'error', json?.error ?? res.statusText);
}

/**
 * Sign in with Discord, the game's flow (ADR-0129, the game's `online/auth.ts`): the start's key kept, Discord's page in a
 * popup, whose callback hands this page a claim (`claims`).
 */
export async function discord(): Promise<void> {
  const { url, key } = await call<AuthStart>('POST', '/api/auth/discord', {});
  localStorage.setItem(KEY, key);
  window.open(new URL(url, API || location.href), 'discord', 'popup,width=520,height=760');
}

/**
 * Calls `signedIn` with each claim's token: one the callback posts this page (a message from the API's origin), or one
 * in the address (`#auth=`: it found no page to post to). A failed end throws through `failed`.
 */
export function claims(signedIn: (token: string) => void, failed: (err: Error) => void): void {
  const claim = async (claim: string) => {
    const key = localStorage.getItem(KEY) ?? '';
    localStorage.removeItem(KEY);
    const end = await call<AuthClaim>('POST', '/api/auth/claim', { key, claim });
    if (end.status !== 'signedIn') throw new Error(end.status === 'failed' ? end.error : 'Not a sign-in');
    signedIn(end.token);
  };
  window.addEventListener('message', (e: MessageEvent<{ auth?: unknown } | null>) => {
    if (e.origin === apiOrigin() && typeof e.data?.auth === 'string') claim(e.data.auth).catch(failed);
  });
  const inAddress = /^#auth=([\w-]{32})$/.exec(location.hash)?.[1];
  if (inAddress) {
    history.replaceState(null, '', location.pathname);
    claim(inAddress).catch(failed);
  }
}
